import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Copy, Mail, ExternalLink, RefreshCw, CheckCircle2, Zap } from "lucide-react";

export const AdminConsentWizard = ({ open, onOpenChange, onConnect }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  const loadStatus = useCallback(async () => {
    setChecking(true);
    try {
      const { data: s } = await api.get("/oauth/microsoft/consent-status");
      setStatus(s);
      return s;
    } catch (err) {
      console.error("consent-status failed", err?.message || err);
      return null;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data: d } = await api.get("/oauth/microsoft/admin-consent-url");
        setData(d);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Errore generazione link consenso");
      }
    })();
    loadStatus();
  }, [open, loadStatus]);

  // Refresh status when the consent popup reports success
  useEffect(() => {
    const handler = (event) => {
      if (event?.data?.type === "microsoft-admin-consent-success") loadStatus();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [loadStatus]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiato negli appunti`);
    } catch (_) {
      window.prompt(`Copia manualmente:`, text);
    }
  };

  const openConsentPopup = () => {
    if (!data?.admin_consent_url) return;
    const w = 600, h = 700;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    window.open(
      data.admin_consent_url,
      "microsoft-admin-consent",
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
    );
  };

  const verify = async () => {
    const s = await loadStatus();
    if (s?.granted) toast.success("Consenso admin registrato — puoi collegare la casella");
    else toast.info("Nessun consenso registrato ancora. Se l'admin l'ha appena dato, riprova tra qualche secondo.");
  };

  const mailtoHref = data
    ? `mailto:?subject=${encodeURIComponent(data.email_subject)}&body=${encodeURIComponent(data.email_body)}`
    : "#";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-consent-wizard">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl flex items-center gap-2">
            <ShieldCheck size={22} className="text-[#0078D4]" />
            Consenso Admin Microsoft 365
          </DialogTitle>
          <DialogDescription>
            Gli account Microsoft 365 aziendali richiedono un&apos;approvazione una tantum
            dell&apos;amministratore. Dopo l&apos;approvazione, tutte le caselle del tenant si collegano normalmente.
          </DialogDescription>
        </DialogHeader>

        {status?.granted && (
          <div className="border border-[#2C4C3B] bg-[#EAF2ED] p-4 flex items-start gap-3" data-testid="consent-granted-banner">
            <CheckCircle2 size={18} className="text-[#2C4C3B] mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <strong>Consenso già concesso</strong>
              {status.granted_at && (
                <span className="block text-xs text-[#5C5B5A] mt-1 font-mono">
                  {new Date(status.granted_at).toLocaleString("it-IT")}
                </span>
              )}
              <Button
                data-testid="consent-connect-now-button"
                onClick={() => { onOpenChange(false); onConnect?.(); }}
                className="mt-3 bg-[#0078D4] hover:bg-[#106EBE] text-white font-mono uppercase text-xs tracking-[0.2em]"
              >
                <Zap size={14} className="mr-2" /> Collega la casella ora
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-5 text-sm">
          {/* Step 1: user IS the admin */}
          <div className="border border-[#E5E4E0] p-4 space-y-3">
            <div className="label-mono text-[#0078D4]">Opzione A — Sei tu l&apos;amministratore</div>
            <p className="text-[#5C5B5A] leading-relaxed">
              Clicca qui sotto, accedi con il tuo <strong>account amministratore</strong> Microsoft 365 e premi
              &ldquo;Accetta&rdquo;. Ci vogliono 2 minuti.
            </p>
            <Button
              data-testid="consent-self-authorize-button"
              onClick={openConsentPopup}
              disabled={!data}
              className="bg-[#0078D4] hover:bg-[#106EBE] text-white font-mono uppercase text-xs tracking-[0.2em] w-full"
            >
              <ExternalLink size={14} className="mr-2" /> Sono io l&apos;admin — Autorizza ora
            </Button>
          </div>

          {/* Step 2: forward to IT admin */}
          <div className="border border-[#E5E4E0] p-4 space-y-3">
            <div className="label-mono text-[#5C5B5A]">Opzione B — Inoltra all&apos;admin IT</div>
            <p className="text-[#5C5B5A] leading-relaxed">
              Invia il link (o l&apos;email già pronta) al tuo amministratore: dovrà aprirlo, accedere con
              l&apos;account admin e accettare. Il link resta valido 7 giorni.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="consent-copy-link-button"
                variant="outline"
                onClick={() => copy(data?.admin_consent_url || "", "Link")}
                disabled={!data}
                className="font-mono uppercase text-xs tracking-[0.15em]"
              >
                <Copy size={14} className="mr-2" /> Copia link
              </Button>
              <Button
                data-testid="consent-copy-email-button"
                variant="outline"
                onClick={() => copy(data ? `${data.email_subject}\n\n${data.email_body}` : "", "Testo email")}
                disabled={!data}
                className="font-mono uppercase text-xs tracking-[0.15em]"
              >
                <Copy size={14} className="mr-2" /> Copia email pronta
              </Button>
              <Button
                data-testid="consent-mailto-button"
                variant="outline"
                asChild
                disabled={!data}
                className="font-mono uppercase text-xs tracking-[0.15em]"
              >
                <a href={mailtoHref}>
                  <Mail size={14} className="mr-2" /> Apri in email
                </a>
              </Button>
            </div>
          </div>

          {/* Step 3: verify */}
          <div className="border border-[#E5E4E0] p-4 space-y-3">
            <div className="label-mono text-[#5C5B5A]">Verifica</div>
            <p className="text-[#5C5B5A] leading-relaxed">
              Dopo che il consenso è stato dato, verifica qui e poi collega la casella.
            </p>
            <Button
              data-testid="consent-verify-button"
              variant="outline"
              onClick={verify}
              disabled={checking}
              className="font-mono uppercase text-xs tracking-[0.15em]"
            >
              <RefreshCw size={14} className={`mr-2 ${checking ? "animate-spin" : ""}`} />
              {checking ? "Verifica…" : "Verifica se il consenso è stato dato"}
            </Button>
            {status && !status.granted && (
              <p className="text-xs text-[#8C8B8A] font-mono" data-testid="consent-not-granted-hint">
                Nessun consenso registrato finora.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
